import * as PIXI from 'pixi.js'

import { BorderStyle } from '../../../layout/constants'
import Settings from '../../settings'

import BackgroundPlane from './BackgroundPlane'
import drawBackground from './drawBackground'

const resources = {}
window.CanvasDrawerResources = resources

const getResource = (sharedResourceId, drawer) => {
  if (sharedResourceId && resources[sharedResourceId]) {
    return resources[sharedResourceId]
  }

  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  const result = {
    drawer,
    canvas,
  }
  if (sharedResourceId) {
    resources[sharedResourceId] = result
  }

  return result
}

/**
 * Drawer for backgrounds with the same border appearance on all sides.
 * Supports borderRadius, boxShadow and dashed borders.
 * Border dash size controlled via borderDashStyle style property.
 */
export default class CanvasDrawer extends PIXI.utils.EventEmitter {
  constructor(sharedResourceId) {
    super()

    this.isDestroyed = false

    this.image = new Image()
    this.image.crossOrigin = 'Anonymous'

    this.resource = getResource(sharedResourceId, this)
    this.canvas = this.resource.canvas

    this.sharedResourceId = sharedResourceId

    this.baseTexture = PIXI.BaseTexture.from(this.canvas, { mipmap: PIXI.MIPMAP_MODES.OFF })
    this.sprite = new PIXI.Sprite(new PIXI.Texture(this.baseTexture))
    this.plane = new BackgroundPlane(new PIXI.Texture(this.baseTexture))
  }

  getElement() {
    return this.sprite
  }

  getElements() {
    return [this.sprite, this.plane]
  }

  draw(style, width, height, imageLoaded) {
    if (!imageLoaded && style.backgroundImage) {
      const src = style.backgroundImage.slice(5, -2)
      this.image.onload = () => {
        if (this.isDestroyed) {
          return
        }

        this.draw(style, width, height, true)
        this.emit('load')
      }
      this.image.src = src
    }

    if (!this.resource.drawer) {
      this.resource.drawer = this
    }

    const useTiles = canUseTiles(style)

    const drawWidth = useTiles ? style.borderLeftSize + 3 + style.borderRightSize : width
    const drawHeight = useTiles ? style.borderTopSize + 3 + style.borderBottomSize : height

    const shouldDraw = this.resource.drawer === this
    if (shouldDraw) {
      drawBackground(
        this.canvas,
        style,
        drawWidth,
        drawHeight,
        imageLoaded ? this.image : undefined,
      )
    }

    if (useTiles) {
      this.sprite.visible = false

      this.plane.visible = true
      this.plane.resize(
        style.boxShadowSizeLeft + width + style.boxShadowSizeRight,
        style.boxShadowSizeTop + height + style.boxShadowSizeBottom,
        style.borderLeftSize + style.boxShadowSizeLeft,
        style.borderTopSize + style.boxShadowSizeTop,
        style.borderRightSize + style.boxShadowSizeRight,
        style.borderBottomSize + style.boxShadowSizeBottom,
      )
      this.plane.x = -style.boxShadowSizeLeft
      this.plane.y = -style.boxShadowSizeTop
    } else {
      this.plane.visible = false

      this.sprite.visible = true
      this.sprite.x = -style.boxShadowSizeLeft
      this.sprite.y = -style.boxShadowSizeTop
    }

    const actualWidth = style.boxShadowSizeLeft + drawWidth + style.boxShadowSizeRight
    const actualHeight = style.boxShadowSizeTop + drawHeight + style.boxShadowSizeBottom
    const resolution = Settings.VIEW_RESOLUTION
    this.baseTexture.setRealSize(actualWidth * resolution, actualHeight * resolution, resolution)
  }

  destroy() {
    this.isDestroyed = true

    if (this.resource.drawer === this) {
      this.resource.drawer = null
    }

    if (!this.sprite._destroyed) {
      this.sprite.destroy({ texture: true, baseTexture: false })
    }
    if (!this.plane._destroyed) {
      this.plane.destroy()
    }

    if (!this.sharedResourceId) {
      this.baseTexture.destroy()
    }
  }
}

export function canUseTiles(style) {
  return style.borderTopStyle !== BorderStyle.DASHED && !style.backgroundImage
}
